// Load/save plumbing for the Branding editor: seeds the mandatory palette
// slots, resolves the brand colours the derivation needs, and builds the
// design_tokens object that is persisted (deltas + palette + accessibility
// target + calculated tokens, every colour normalised to oklch()).

import { colorFamily } from "@cms/ui/lib/color";
import { toOklch } from "@cms/ui/lib/oklch";
import {
  BASE_BACKGROUND_SLOT,
  MANDATORY_PALETTE_SLOTS,
  PRIMARY_BRAND_SLOT,
  isPaletteTextKey,
  paletteFromTokens,
  tokensWithPalette,
} from "@cms/portal-lib/palette";
import {
  A11Y_TARGET_TOKEN,
  DERIVED_TOKEN_KEYS,
  LEGACY_TOKEN_KEYS,
  TOKEN_DEFAULTS,
  type A11yTarget,
} from "./design-tokens";
import { deriveTokens, deriveTypographyTokens } from "./derived-tokens";

const DERIVED_KEY_SET = new Set<string>([...DERIVED_TOKEN_KEYS, ...LEGACY_TOKEN_KEYS]);

/** The tenant's minimum (mobile) heading scale ratio (typography-scale override or the default). */
export function resolveTypographyScale(overrides: Record<string, string>): number {
  const raw = Number.parseFloat(overrides["typography-scale"] ?? "");
  return Number.isFinite(raw) ? raw : Number.parseFloat(TOKEN_DEFAULTS["typography-scale"]);
}

/**
 * The maximum (desktop) scale ratio. Falls back to the minimum scale — so
 * tenants that never set one keep static, non-fluid sizes.
 */
export function resolveTypographyScaleMax(overrides: Record<string, string>): number {
  const raw = Number.parseFloat(overrides["typography-scale-max"] ?? "");
  return Number.isFinite(raw) ? raw : resolveTypographyScale(overrides);
}

/**
 * Prepare freshly-loaded overrides for editing: drop calculated tokens (they
 * are recomputed live) and retired legacy tokens, migrate any legacy `bg`
 * override into the Base Background slot, and seed the two mandatory palette
 * slots.
 */
export function sanitizeLoadedOverrides(raw: Record<string, string>): Record<string, string> {
  const overrides: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (DERIVED_KEY_SET.has(key) || isPaletteTextKey(key)) continue;
    overrides[key] = value;
  }

  const palette = paletteFromTokens(overrides);
  if (!palette[PRIMARY_BRAND_SLOT]?.trim()) {
    palette[PRIMARY_BRAND_SLOT] = overrides.accent ?? TOKEN_DEFAULTS.accent;
  }
  if (!palette[BASE_BACKGROUND_SLOT]?.trim()) {
    palette[BASE_BACKGROUND_SLOT] = overrides.bg ?? TOKEN_DEFAULTS.bg;
  }
  delete overrides.bg; // bg is owned by the Base Background Colour slot now
  return tokensWithPalette(overrides, palette.map((color) => toOklch(color)));
}

export interface BrandColors {
  accent: string;
  bg: string;
  headerBg: string;
  footerBg: string;
  palette: string[];
}

/** The concrete brand colours after inheritance (accent ← slot 1, bg ← slot 2). */
export function resolveBrandColors(overrides: Record<string, string>): BrandColors {
  const palette = paletteFromTokens(overrides);
  const primary = palette[PRIMARY_BRAND_SLOT]?.trim() || TOKEN_DEFAULTS.accent;
  const baseBg = palette[BASE_BACKGROUND_SLOT]?.trim() || TOKEN_DEFAULTS.bg;
  return {
    accent: overrides.accent?.trim() || primary,
    bg: baseBg,
    headerBg: overrides["header-bg"]?.trim() || TOKEN_DEFAULTS["header-bg"],
    footerBg: overrides["footer-bg"]?.trim() || TOKEN_DEFAULTS["footer-bg"],
    palette,
  };
}

/** Editable values + inherited brand colours + calculated tokens, for preview. */
export function previewTokens(
  overrides: Record<string, string>,
  target: A11yTarget
): Record<string, string> {
  const brand = resolveBrandColors(overrides);
  return {
    ...TOKEN_DEFAULTS,
    ...overrides,
    accent: brand.accent,
    bg: brand.bg,
    ...deriveTokens(brand, target),
    ...deriveTypographyTokens(
      resolveTypographyScale(overrides),
      resolveTypographyScaleMax(overrides)
    ),
  };
}

/**
 * The design_tokens object to persist: non-default editable overrides, the
 * palette, the accessibility target and every calculated token. All colour
 * values are stored as oklch().
 */
export function buildPersistedTokens(
  overrides: Record<string, string>,
  target: A11yTarget
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (!trimmed || trimmed === TOKEN_DEFAULTS[key]) continue;
    out[key] = trimmed;
  }

  const brand = resolveBrandColors(overrides);
  // Mandatory palette slots always persist so accent/bg inheritance and picker
  // presets survive a reload even when they match the theme defaults.
  const palette = brand.palette.slice();
  while (palette.length < MANDATORY_PALETTE_SLOTS) palette.push("");
  palette[PRIMARY_BRAND_SLOT] = palette[PRIMARY_BRAND_SLOT]?.trim() || TOKEN_DEFAULTS.accent;
  palette[BASE_BACKGROUND_SLOT] = palette[BASE_BACKGROUND_SLOT]?.trim() || TOKEN_DEFAULTS.bg;

  const withPalette = tokensWithPalette(out, palette);
  withPalette.accent = brand.accent;
  withPalette.bg = brand.bg;
  Object.assign(withPalette, deriveTokens({ ...brand, palette }, target));
  Object.assign(
    withPalette,
    deriveTypographyTokens(resolveTypographyScale(overrides), resolveTypographyScaleMax(overrides))
  );
  withPalette[A11Y_TARGET_TOKEN] = target;

  // Normalise every colour-shaped value to canonical oklch().
  const normalised: Record<string, string> = {};
  for (const [key, value] of Object.entries(withPalette)) {
    normalised[key] = colorFamily(value) ? toOklch(value) : value;
  }
  return normalised;
}
